import { GROUP_BY_COLUMN_DATA } from '../../shared/constants';
import { alignRightCellStyle, alignCenterCellStyle } from '@common/cellStyles';
import {
  legalEntityColumnDefinition,
  businessUnitColumnDefinition,
  countryTaxTypeColumnDefinition,
} from '../../../shared/columnDefinitions';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import TaxExpenseTooltipRenderer from '../drillDetailLinkCellRenderer.component';
import { customNumeralValueFormatter } from '../../../shared/customNumeralValueFormatter';
import { NumeralTypes } from '@common/utils/formatNumberValue';
import { numeralValueFormatter } from '../../../shared/numeralValueFormatter';

export default ({
  groupByName,
  groupByField,
  groupItem,
  hasSourceCurrency,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
}) => [
  {
    headerName: groupByName,
    field: groupByField,
    minWidth: 120,
    pinned: 'left',
    ...alignCenterCellStyle,
  },
  ...(groupByName === GROUP_BY_COLUMN_DATA.CC.groupByName
    ? [
      { ...legalEntityColumnDefinition, headerName: 'Legal Entity', field: 'LEGAL_ENTITY' },
      { ...businessUnitColumnDefinition, field: 'BUSINESS_UNIT' },
    ]
    : []
  ),
  ...(groupItem
    ? [{ ...countryTaxTypeColumnDefinition, headerName: 'Country Tax Type', field: 'COUNTRY_TAX_TYPE' }]
    : []
  ),
  {
    headerName: 'Currency',
    field: 'FUNC_CURRENCY',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'Status',
    field: 'STATUS_NAME',
    minWidth: 120,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'Federal Taxable Income/(Loss) Before State',
    field: 'US_TXBL_INC_B4_STATE',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total State Tax Permanent Modifications',
    field: 'STATE_PERM_MODS',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total State Tax Temporary Modifications',
    field: 'STATE_TEMP_MODS',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: `State Taxable Income/(Loss) Before Pre-Apportionment NOL`,
    field: 'STATE_TXBL_INC',
    minWidth: 160,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: `Utilize Prior Year NOL's pre Apportionment`,
    field: 'UTILIZE_PY_NOL_PRE',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'State Taxable Inc/(Loss) before Apportionment',
    field: 'STATE_TXBL_INC_B4_AP',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Apportionment Rate',
    field: 'AP_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Apportioned Taxable Income/(Loss)',
    field: 'AP_TXBL_INC',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: `Utilize Prior Year NOL's post Apportionment`,
    field: 'UTILIZE_PY_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Taxable Income After NOL Utilization',
    field: 'TXBL_INC_AFT_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Record Current Year NOL',
    field: 'RECORD_CY_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Rate',
    field: 'TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense Before Credits',
    field: 'CUR_TAX_B4_CR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Minimum Tax',
    field: 'STATE_MIN_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total',
    field: 'MAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Credits and Other After Tax Adj',
    field: 'TOTAL_CR',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Federal CY Temporary Adjustments',
    field: 'CY_TEMP',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'State Modifications',
    field: 'STATE_MODS',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Net Operating NOL',
    field: 'NO_NOL',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Record Valuation Allowance',
    field: 'RECORD_VA',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Deferreds',
    field: 'TOT_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Rate',
    field: 'DEF_TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense',
    field: 'DEF_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total After Tax Deferred Tax Expense',
    field: 'DEF_PR_CR_UTL',
    minWidth: 120,
    cellRenderer: TaxExpenseTooltipRenderer,
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense on CY Activity',
    field: 'CUR_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense on CY Activity',
    field: 'TOT_DEF_TAX',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: !hasSourceCurrency
      ? 'Tax Expense on CY Activity'
      : 'Tax Expense on CY Activity (Functional Currency)',
    field: 'TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'CY Effective Tax Rate',
    field: 'CY_EFF_TAX_RATE',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Currency',
    field: 'TAX_CURRENCY',
    minWidth: 120,
    hide: !hasSourceCurrency,
    ...alignCenterCellStyle,
  },
  {
    headerName: 'MOR',
    field: 'TAX_CUR_MOR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense on CY Activity',
    field: 'CUR_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense on CY Activity',
    field: 'DEF_TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Tax Expense - CY Activity (Tax Currency)',
    field: 'TAX_EXP_CY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'RES_CUR_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'RES_NON_CUR_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'RES_PEN_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Interest)',
    field: 'RES_INT_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Current)',
    field: 'OTH_PROV_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Other - CY Activity (Current) - Tax Currency',
    field: 'TOT_CUR_AFT_TAX_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Deferred)',
    field: 'OTH_PROV_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Other - CY Activity (Deferred) - Tax Currency',
    field: 'TOT_DEF_AFT_TAX_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Current Tax Expense on CY Activity - Tax Currency',
    field: 'TOT_CUR_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Deferred Tax Expense on CY Activity - Tax Currency',
    field: 'TOT_DEF_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Expense on CY Activity - Tax Currency',
    field: 'TOT_TAX_EXP_CY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Current)',
    field: 'RTA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Current)',
    field: 'PRA_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Payable)',
    field: 'TRUE_UP_PAY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Current)',
    field: 'RES_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Non Current)',
    field: 'RES_NON_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Interest)',
    field: 'RES_INT',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reserve (Penalty)',
    field: 'RES_PEN',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Current Tax Expense - PY Activity - Tax Currency',
    field: 'TOT_CUR_AFT_TAX_PY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Deferred)',
    field: 'RTA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustments (Deferred)',
    field: 'PRA_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'True Up (Deferred)',
    field: 'TRUE_UP_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Rate Changes (Deferred)',
    field: 'RATE_CHG_DEF',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Deferred Tax Expense - PY Activity - Tax Currency',
    field: 'TOT_DEF_AFT_TAX_PY',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Total Tax Expense on PY Activity - Tax Currency',
    field: 'TAX_EXP_PY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Current Tax Expense',
    field: 'CUR_TAX_EXP_PY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Deferred Tax Expense',
    field: 'DEF_TAX_EXP_PY_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: !hasSourceCurrency
      ? 'Total Tax Expense'
      : 'Total Tax Expense (Tax Currency)',
    field: 'TAX_EXP_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: !hasSourceCurrency
      ? 'Adjusted Income/(Loss) Before Tax'
      : 'Adjusted Income/(Loss) Before Tax (Tax Currency)',
    field: 'IBT_TAX_CUR',
    minWidth: 120,
    valueFormatter: customNumeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.DECIMAL,
    }),
    exportFormat: CellExportFormats.NUMBER,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Effective Tax Rate',
    field: 'ETR',
    minWidth: 120,
    valueFormatter: numeralValueFormatter({
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
      type: NumeralTypes.PERCENTAGE,
    }),
    exportFormat: CellExportFormats.PERCENT_6,
    omitExportIfNull: true,
    ...alignRightCellStyle,
  },
];
